// Turn flow. Every function takes a state and returns a new one. The store
// wraps these; the UI never calls them directly.

import {
  CAPTURE_AGAIN_SPELL_ID,
  CAST_STOP_TURNS_CASTER,
  ENERGY_PER_TURN,
  HISTORY_PLIES,
  KING_ENERGY_CAP,
  PASS_LIMIT,
  QUIET_PLY_LIMIT,
  ROOK_ENERGY_CAP,
  WHEEL_REFRESH,
} from '../constants';
import {
  CASTERS,
  CASTERS_PER_SIDE,
  type CasterId,
  casterById,
  ENERGY_DIE,
} from '../content/casters';
import { addAbilities, BASE_ABILITIES, KIND_NAMES } from '../engine/abilities';
import { applyMove, bothCrownsFallen, countSidePieces, detonate, winnerOf } from '../engine/apply';
import { describePiece, OWNER_NAMES } from '../engine/describe';
import { generateMoves, isMoveLegal, movesForPiece } from '../engine/moves';
import { createInitialBoard, findPieceSquare, otherSide, withPiece } from '../engine/pieces';
import { addEnergy, livingCasterIds, livingPitches, spendEnergy } from '../engine/roles';
import { neighborSquares, squareName } from '../engine/squares';
import { TERRAIN_NAMES, tickTerrain } from '../engine/terrain';
import { expiringTraits, tickTimers } from '../engine/traits';
import type {
  Board,
  Move,
  MoveOutcome,
  Note,
  Piece,
  PieceKind,
  Pitch,
  Position,
  Rules,
  Side,
} from '../engine/types';
import type { Rng } from '../lib/rng';
import { addLog, recordEvent } from '../spells/helpers';
import { spellById } from '../spells/library';
import { gainScroll } from '../spells/scrolls';
import type { PickRegion, Spell, SpellId } from '../spells/types';
import { answerFor, dealWheel, replacementFor, spentAnswer } from '../spells/wheel';
import {
  armyStrength,
  canCastSlot,
  casterGroupsOf,
  isAtHalfStrength,
  scrollsByCaster,
  slotsOf,
  waitsForHalfStrength,
} from './casters';
import {
  canPayForRoll,
  DEFAULT_MATCH_MODE,
  type GameState,
  type MatchMode,
  rollCost,
  sideTurnNumber,
} from './state';

const SIDES: readonly Side[] = ['gold', 'violet'];

export const positionOf = (state: GameState): Position => ({
  board: state.board,
  rules: state.rules,
});

/**
 * The serial of the next cast. A roll of the wheel gives the cast its own
 * serial, so the hand knows the card of the roll was cast. A cast from a slot
 * has no roll, and counts the casts.
 */
const castSerial = (state: GameState): number =>
  state.mode === 'gods' ? state.castCount + 1 : (state.lastRoll?.serial ?? 0);

/** A spell with nothing to do. It is recorded as a cast so the narrator says so. */
const recordNoEffect = (state: GameState, spell: Spell, reason: string): GameState => {
  const line = `${spell.name} had no effect. ${reason}`;
  return {
    ...state,
    log: [
      ...state.log,
      {
        id: state.nextLogId,
        ply: state.ply,
        tone: 'system',
        text: line,
        spell: spell.name,
        spellId: spell.id,
      },
    ],
    nextLogId: state.nextLogId + 1,
    hadNoEffect: false,
    castCount: state.castCount + 1,
    lastCast: {
      serial: castSerial(state),
      spellId: spell.id,
      caster: state.turn,
      source: null,
      squares: [],
      line,
      hadNoEffect: true,
      steps: [],
      firstEvent: state.nextEventSerial,
    },
  };
};

const lastEventText = (state: GameState): string | undefined =>
  state.events[state.events.length - 1]?.text;

/**
 * The casters of a match of Godsmode. Gold has `goldIds`, or draws
 * `CASTERS_PER_SIDE` of them. Violet has `violetIds`, or draws as many from
 * the casters gold does not have. Rob, 2026-10-12: "Random each match, not the
 * same caster", and later "Now you pick TWO casters".
 */
const dealCasters = (
  rng: Rng,
  goldIds: readonly CasterId[] | null,
  violetIds: readonly CasterId[] | null,
): Record<Side, readonly CasterId[]> => {
  const draw = (taken: readonly CasterId[]): CasterId[] =>
    rng
      .shuffle(CASTERS.filter((caster) => !taken.includes(caster.id)))
      .slice(0, CASTERS_PER_SIDE)
      .map((caster) => caster.id);
  const gold = goldIds ?? draw([]);
  const violet = violetIds ?? draw(gold);
  return { gold, violet };
};

/** The squares of the pieces of a side of one kind, from the left. */
const squaresOfKind = (board: Board, side: Side, kind: PieceKind): number[] =>
  board.flatMap((piece, square) => (piece?.owner === side && piece.kind === kind ? [square] : []));

const PITCHES: readonly Pitch[] = ['careful', 'aggro'];

/**
 * Gives the pieces their roles at the start of a match of Godsmode. Each
 * caster of a side goes to a random pawn of the side. Each rook and the king
 * hold energy. Each knight gives one of the two pitches, at random.
 * Changed 2026-10-15. Rob: "casters are assigned randomly to pawns instead of
 * to bishops", and "The king should also carry 2 energy, in the same way that
 * the rooks carry 3 each." The casters were on the bishops from 2026-10-13.
 */
const dealRoles = (board: Board, casters: Record<Side, readonly CasterId[]>, rng: Rng): Board => {
  const next = [...board];
  const give = (square: number | undefined, role: Partial<Piece>): void => {
    const piece = square === undefined ? null : next[square];
    if (square !== undefined && piece) next[square] = { ...piece, ...role };
  };
  for (const side of SIDES) {
    const pawns = rng.shuffle(squaresOfKind(board, side, 'pawn'));
    for (const [index, casterId] of casters[side].entries()) {
      give(pawns[index], { casterId });
    }
    for (const rook of squaresOfKind(board, side, 'rook')) {
      give(rook, { energyCap: ROOK_ENERGY_CAP });
    }
    for (const king of squaresOfKind(board, side, 'king')) {
      give(king, { energyCap: KING_ENERGY_CAP });
    }
    const knights = squaresOfKind(board, side, 'knight');
    for (const [index, pitch] of rng.shuffle(PITCHES).entries()) {
      give(knights[index], { pitch });
    }
  }
  return next;
};

/**
 * `casterIds` are the casters of gold in Godsmode. With none, the match
 * draws them. `opponentIds` are the casters of violet, for a simulation. A
 * match of the game leaves them out, and the match draws them.
 */
export const createMatch = (
  seed: number,
  rng: Rng,
  mode: MatchMode = DEFAULT_MATCH_MODE,
  casterIds: readonly CasterId[] | null = null,
  opponentIds: readonly CasterId[] | null = null,
): GameState => {
  const { board: plain, nextPieceId } = createInitialBoard();
  const withCasters = mode === 'gods';
  const casters = withCasters ? dealCasters(rng, casterIds, opponentIds) : null;
  const board = casters ? dealRoles(plain, casters, rng) : plain;
  const fresh: GameState = {
    seed,
    mode,
    board,
    rules: { pawnsRetreat: false, noCapturePlies: 0, terrain: {}, captureSpoils: withCasters },
    turn: 'gold',
    ply: 0,
    phase: 'move',
    // A match with casters has no wheel. The die gives energy.
    wheel: withCasters ? [] : dealWheel(rng, board),
    wheelFaceToRefresh: null,
    lastRoll: null,
    lastCast: null,
    events: [],
    nextEventSerial: 1,
    pendingSpell: null,
    pendingTargets: [],
    pendingRolls: 0,
    energy: { gold: 0, violet: 0 },
    paidRolls: { gold: 0, violet: 0 },
    energyHold: { gold: 0, violet: 0 },
    rollDiscounts: { gold: 0, violet: 0 },
    casters,
    scrolls: { gold: [], violet: [] },
    halfStrength: { gold: false, violet: false },
    startStrength: armyStrength(board, 'gold'),
    skillCasts: { gold: 0, violet: 0 },
    castCount: 0,
    pendingSlot: null,
    hadNoEffect: false,
    castSteps: [],
    passes: 0,
    quietPlies: 0,
    piecesAtPlyEnd: countSidePieces(board),
    freeRollUsed: false,
    mustMovePieceId: null,
    suggestFor: null,
    moveAgainSpellId: null,
    lastMove: null,
    winner: null,
    endLine: '',
    log: [],
    captured: [],
    boardHistory: [],
    nextPieceId,
    nextLogId: 1,
  };
  const announced = addLog(
    fresh,
    'system',
    `A new match. Seed ${seed}. ${OWNER_NAMES.gold} moves first.`,
  );
  return beginTurn(announced, rng);
};

// ---- Turn start --------------------------------------------------------------

const WAKE_KINDS: readonly PieceKind[] = ['knight', 'bishop', 'rook', 'queen'];

const recordNotes = (state: GameState, notes: readonly Note[]): GameState =>
  notes.reduce(
    (acc, note) =>
      recordEvent(acc, note.caption, note.text, note.squares, 'capture', [], note.energy),
    state,
  );

/** Takes the board of a move, and gives each copy from a clone square its own id. */
const withMoveBoard = (state: GameState, outcome: MoveOutcome): GameState => {
  let board = outcome.board;
  let nextPieceId = state.nextPieceId;
  for (const square of outcome.clones) {
    const copy = board[square];
    if (!copy) continue;
    board = withPiece(board, square, { ...copy, id: nextPieceId });
    nextPieceId += 1;
  }
  return { ...state, board, nextPieceId };
};

/** Counts every timer on a side's pieces down one turn, and fires what hits zero. */
const tickPieces = (state: GameState, side: Side, rng: Rng): GameState => {
  let next = state;
  const before = state.board;
  const board: Board = before.map((piece) => (piece?.owner === side ? tickTimers(piece) : piece));
  next = { ...next, board };

  // Timers that run out this turn, as one event.
  const expired: { square: number; text: string }[] = [];
  before.forEach((piece, square) => {
    if (!piece || piece.owner !== side) return;
    for (const trait of expiringTraits(piece)) {
      expired.push({ square, text: `${describePiece(piece, square)} ${trait.expiry}.` });
    }
  });
  if (expired.length > 0) {
    next = recordEvent(
      next,
      expired.length === 1 ? 'A spell wears off' : 'Spells wear off',
      expired.map((entry) => entry.text).join(' '),
      expired.map((entry) => entry.square),
    );
  }

  // Eggs wake.
  before.forEach((piece, square) => {
    if (!piece || piece.owner !== side || piece.dormantTurns !== 1) return;
    const current = next.board[square];
    if (!current) return;
    const kind = rng.pick(WAKE_KINDS);
    const woke: Piece = {
      ...current,
      kind,
      abilities: addAbilities(BASE_ABILITIES[kind], current.gained),
      dormantTurns: 0,
    };
    next = { ...next, board: withPiece(next.board, square, woke) };
    next = recordEvent(
      next,
      'A dormant piece wakes',
      `The dormant on ${squareName(square)} woke. It is a ${KIND_NAMES[kind]}.`,
      [square],
    );
  });

  // Fuses go off.
  before.forEach((piece, square) => {
    if (!piece || piece.owner !== side || piece.explodeIn !== 1) return;
    if (next.board[square]?.id !== piece.id) return;
    const blast = detonate(next.board, square);
    next = { ...next, board: blast.board, captured: [...next.captured, ...blast.captured] };
    const count = blast.captured.length;
    next = recordEvent(
      next,
      'A piece explodes',
      `${describePiece(piece, square)} exploded. ${count} piece${count === 1 ? '' : 's'} gone.`,
      blast.squares,
    );
  });

  // Copies come.
  before.forEach((piece) => {
    if (!piece || piece.owner !== side || piece.cloneIn !== 1) return;
    const square = findPieceSquare(next.board, piece.id);
    const original = square === null ? null : next.board[square];
    if (square === null || !original) return;
    const who = describePiece(original, square);
    const open = neighborSquares(square).filter(
      (neighbor) => next.board[neighbor] === null && !next.rules.terrain[neighbor],
    );
    if (open.length === 0) {
      next = recordEvent(
        next,
        'No room for a copy',
        `${who} has no empty square beside it. No copy.`,
        [square],
      );
      return;
    }
    const spot = rng.pick(open);
    const copy: Piece = { ...original, id: next.nextPieceId, home: spot, movesTwice: false };
    next = {
      ...next,
      board: withPiece(next.board, spot, copy),
      nextPieceId: next.nextPieceId + 1,
    };
    next = recordEvent(
      next,
      'A piece is cloned',
      `A copy of ${who} stands on ${squareName(spot)}.`,
      [square, spot],
      'ally',
    );
  });

  // Sleepwalkers wander.
  before.forEach((piece) => {
    if (!piece || piece.owner !== side || piece.randomMoveTurns === 0) return;
    const current = findPieceSquare(next.board, piece.id);
    if (current === null) return;
    const moves = movesForPiece(positionOf(next), current);
    if (moves.length === 0) return;
    const move = rng.pick(moves);
    const outcome = applyMove(positionOf(next), move);
    next = {
      ...withMoveBoard(next, outcome),
      captured: [...next.captured, ...outcome.captured],
    };
    const victim = outcome.captured[0];
    const stepped = victim ? ` It took ${describePiece(victim, move.to)}.` : '';
    next = recordEvent(
      next,
      'A piece moves at random',
      `${describePiece(piece, current)} moved at random to ${squareName(move.to)}.${stepped}`,
      [current, move.to],
    );
    next = recordNotes(next, outcome.notes);
  });

  return next;
};

const tickGround = (state: GameState): GameState => {
  const { rules, expired } = tickTerrain(state.rules);
  let next: GameState = { ...state, rules };
  // Each turn of the rule is a beat, so its seals and its band change on a caption and not with no word.
  if (rules.noCapturePlies > 0) {
    const left = rules.noCapturePlies - 1;
    next = { ...next, rules: { ...rules, noCapturePlies: left } };
    next =
      left === 0
        ? recordEvent(next, 'Captures again', 'Pieces can capture again.', [])
        : recordEvent(next, 'No captures', 'No piece can capture this turn.', []);
  }
  for (const square of expired) {
    const kind = state.rules.terrain[square]?.kind;
    if (kind) {
      next = addLog(next, 'timer', `The ${TERRAIN_NAMES[kind]} on ${squareName(square)} is gone.`);
    }
  }
  return next;
};

const remember = (state: GameState): GameState => ({
  ...state,
  boardHistory: [...state.boardHistory, state.board].slice(-HISTORY_PLIES),
});

/** A spell holds the energy of the side to move this turn. One turn of the hold goes. */
const holdEnergy = (state: GameState): GameState => {
  const side = state.turn;
  const left = state.energyHold[side] - 1;
  const after =
    left === 0 ? 'It gains energy again next turn.' : `${left} more turn${left === 1 ? '' : 's'}.`;
  return recordEvent(
    { ...state, energyHold: { ...state.energyHold, [side]: left } },
    'No energy this turn',
    `${OWNER_NAMES[side]} gains no energy this turn. ${after}`,
    [],
  );
};

/** The side to move gains energy, unless a spell holds it. It may pay for a roll before its move. */
const gainEnergy = (state: GameState): GameState => {
  const side = state.turn;
  if (state.energyHold[side] > 0) return holdEnergy(state);
  return { ...state, energy: { ...state.energy, [side]: state.energy[side] + ENERGY_PER_TURN } };
};

/**
 * A skill that waits for half strength unlocks when the army of its side is
 * there, and stays unlocked. Rob, 2026-10-12: "unlocks are permanent even if
 * the army gains pieces and goes back over half later".
 */
const unlockAtHalfStrength = (state: GameState): GameState => {
  if (!state.casters) return state;
  let next = state;
  for (const side of SIDES) {
    if (next.halfStrength[side] || !isAtHalfStrength(next, side)) continue;
    next = { ...next, halfStrength: { ...next.halfStrength, [side]: true } };
    const waiting = state.casters[side]
      .flatMap((id) => casterById(id).skills)
      .filter(waitsForHalfStrength);
    for (const skill of waiting) {
      next = recordEvent(
        next,
        'A skill unlocks',
        `The army of ${OWNER_NAMES[side]} is at half strength. ${spellById(skill.spellId).name} unlocks.`,
        [],
        'system',
      );
    }
  }
  return next;
};

/**
 * The start of a turn in Godsmode. The side rolls the die for its energy,
 * unless a spell holds the energy this turn. Then it casts, or not, and its
 * pieces move.
 */
const beginCasterTurn = (state: GameState, rng: Rng): GameState => {
  const unlocked = unlockAtHalfStrength(state);
  if (unlocked.energyHold[unlocked.turn] > 0) return enterMovePhase(holdEnergy(unlocked), rng);
  return { ...unlocked, phase: 'roll', pendingRolls: 1 };
};

const beginTurn = (state: GameState, rng: Rng): GameState => {
  let next = remember(state);
  next = tickGround(next);
  next = tickPieces(next, next.turn, rng);
  const ended = settleCrowns(next, lastEventText(next) ?? 'The rules did it.');
  if (ended) return ended;
  if (next.mode === 'gods') return beginCasterTurn(next, rng);
  return enterMovePhase(gainEnergy(next), rng);
};

const enterMovePhase = (state: GameState, rng: Rng): GameState => {
  const moves = generateMoves(positionOf(state), state.turn);
  if (moves.length === 0) {
    const passed = addLog(
      { ...state, passes: state.passes + 1 },
      'system',
      `${OWNER_NAMES[state.turn]} has no legal move and passes.`,
    );
    if (passed.passes >= PASS_LIMIT) {
      const line = 'Neither side can move.';
      return {
        ...addLog(passed, 'system', `${line} A draw.`),
        winner: null,
        endLine: line,
        phase: 'over',
      };
    }
    return endTurn(passed, rng);
  }
  return { ...state, phase: 'move' };
};

const replaceFace = (state: GameState, face: number, replacement: SpellId): GameState => {
  const wheel = state.wheel.map((id, index) => (index === face ? replacement : id));
  return addLog({ ...state, wheel }, 'wheel', `New card: ${spellById(replacement).name}.`);
};

/**
 * A rolled face keeps its spell, or gets a new one when the wheel refreshes.
 * Either way, a spell that answers the face takes it. Then a face that
 * answers a spell and has nothing left to answer gives its place back,
 * rolled or not.
 */
const refreshWheel = (state: GameState, rng: Rng): GameState => {
  const face = state.wheelFaceToRefresh;
  const turn = sideTurnNumber(state);
  const replacement =
    face === null
      ? null
      : WHEEL_REFRESH
        ? replacementFor(state.wheel, face, rng, state.board, turn)
        : answerFor(state.wheel, face, state.board, turn);
  const rolled =
    face === null || replacement === null ? state : replaceFace(state, face, replacement);
  const spent = spentAnswer(rolled.wheel, rolled.board, turn);
  const next = spent === null ? rolled : replaceFace(rolled, spent.face, spent.replacement);
  return { ...next, wheelFaceToRefresh: null };
};

const endTurn = (state: GameState, rng: Rng): GameState => {
  if (state.winner) return state;
  const refreshed = refreshWheel(state, rng);
  const piecesAtPlyEnd = countSidePieces(refreshed.board);
  const quietPlies = piecesAtPlyEnd === state.piecesAtPlyEnd ? state.quietPlies + 1 : 0;
  if (quietPlies >= QUIET_PLY_LIMIT) {
    const line = `No piece came or went in ${QUIET_PLY_LIMIT / 2} turns of each side.`;
    return {
      ...addLog(refreshed, 'system', `${line} A draw.`),
      quietPlies,
      winner: null,
      endLine: line,
      phase: 'over',
    };
  }
  return beginTurn(
    {
      ...refreshed,
      quietPlies,
      piecesAtPlyEnd,
      turn: otherSide(state.turn),
      ply: state.ply + 1,
      freeRollUsed: false,
      mustMovePieceId: null,
      suggestFor: null,
      moveAgainSpellId: null,
      pendingSpell: null,
      pendingTargets: [],
    },
    rng,
  );
};

// ---- Rolling and casting -----------------------------------------------------

/**
 * A roll the side is owed costs nothing. Any other roll comes before the
 * move and costs energy, and the next one costs more.
 */
export const rollDie = (state: GameState, rng: Rng): GameState => {
  if (state.mode === 'gods') return rollEnergy(state, rng);
  const isOwed = state.phase === 'roll';
  if (!isOwed && !canPayForRoll(state)) return state;
  const side = state.turn;
  const cost = isOwed ? 0 : rollCost(state, side);
  const face = rng.int(state.wheel.length);
  const spellId = state.wheel[face];
  if (spellId === undefined) return state;
  const spell = spellById(spellId);
  const serial = (state.lastRoll?.serial ?? 0) + 1;
  const rolled: GameState = {
    ...state,
    lastRoll: {
      serial,
      face,
      spellId,
      gain: null,
      firstLogId: state.nextLogId,
      caster: state.turn,
      ply: state.ply,
    },
    pendingRolls: isOwed ? state.pendingRolls - 1 : state.pendingRolls,
    energy: { ...state.energy, [side]: state.energy[side] - cost },
    paidRolls: isOwed ? state.paidRolls : { ...state.paidRolls, [side]: state.paidRolls[side] + 1 },
    // A roll the side paid for uses one discount. A roll it is owed does not.
    rollDiscounts: isOwed
      ? state.rollDiscounts
      : { ...state.rollDiscounts, [side]: Math.max(0, state.rollDiscounts[side] - 1) },
    wheelFaceToRefresh: face,
  };
  const paid = isOwed ? '' : ` for ${cost} energy`;
  const logged = addLog(rolled, 'roll', `${OWNER_NAMES[side]} pulled ${spell.name}${paid}.`);
  return beginCast(logged, spell, rng);
};

/**
 * The roll of a turn in Godsmode. A face gives energy, or a scroll for an
 * open scroll slot. With no open slot the scroll face gives nothing.
 */
const rollEnergy = (state: GameState, rng: Rng): GameState => {
  if (state.phase !== 'roll') return state;
  const side = state.turn;
  const face = rng.int(ENERGY_DIE.length);
  const value = ENERGY_DIE[face] ?? 0;
  const serial = (state.lastRoll?.serial ?? 0) + 1;
  const name = OWNER_NAMES[side];
  let next: GameState = { ...state, pendingRolls: 0 };
  let scroll: SpellId | null = null;
  let gain = 0;
  if (value === 'scroll') {
    const gained = gainScroll(next, side, rng);
    next = gained.state;
    scroll = gained.scroll;
    next = addLog(
      next,
      'roll',
      scroll
        ? `${name} cast the die and got a scroll: ${spellById(scroll).name}.`
        : `${name} cast the die and got a scroll, with no open slot for it.`,
    );
  } else {
    // The pieces of the side hold the energy. What finds no room is lost.
    const given = addEnergy(next.board, side, value);
    gain = given.stored;
    next = { ...next, board: given.board };
    const lost = value - gain;
    next = addLog(
      next,
      'roll',
      lost === 0
        ? `${name} cast the die and gained ${gain} energy.`
        : `${name} cast the die and gained ${gain} energy. ${lost} more had no room.`,
    );
  }
  const rolled: GameState = {
    ...next,
    lastRoll: {
      serial,
      face,
      spellId: scroll,
      gain,
      firstLogId: state.nextLogId,
      caster: side,
      ply: state.ply,
    },
  };
  return enterMovePhase(rolled, rng);
};

/**
 * Casts from a slot of the side to move, in Godsmode. A spell with a target
 * waits for the aim. The slot is paid for when the spell lands: a skill
 * costs its energy, and a scroll is used up.
 */
export const castSlot = (state: GameState, index: number, rng: Rng): GameState => {
  if (!canCastSlot(state, index)) return state;
  const slot = slotsOf(state, state.turn)[index];
  if (!slot || slot.spellId === null) return state;
  const spell = spellById(slot.spellId);
  // The log says who casts, and with what. The lines of the spell say what it did.
  const price = slot.kind === 'scroll' ? 'from a scroll' : `for ${slot.cost} energy`;
  const said = addLog(state, 'roll', `${OWNER_NAMES[state.turn]} casts ${spell.name} ${price}.`);
  const held: GameState = { ...said, pendingSlot: index };
  if (spell.targetCount > 0) {
    return { ...held, phase: 'aim', pendingSpell: spell.id, pendingTargets: [] };
  }
  return continueAfterCast(resolveCast(held, spell, rng, []), rng);
};

/**
 * Takes back a cast from a slot that waits for its picks. Nothing was paid:
 * a slot is paid when the spell lands. The picks made so far go, and the
 * side may move or cast again. A roll of the wheel cannot be taken back.
 * Garrett, 2026-10-14: "there should also be a button to back out or some way
 * to back out of this view so I don't have to play the spell by default".
 */
export const cancelCast = (state: GameState): GameState => {
  if (state.phase !== 'aim' || state.pendingSlot === null || !state.pendingSpell) return state;
  const spell = spellById(state.pendingSpell);
  const said = addLog(state, 'roll', `${OWNER_NAMES[state.turn]} takes back ${spell.name}.`);
  return { ...said, phase: 'move', pendingSlot: null, pendingSpell: null, pendingTargets: [] };
};

/** Takes the price of the slot the cast came from: the energy of a skill, or the scroll. */
const payForSlot = (state: GameState): GameState => {
  const index = state.pendingSlot;
  if (index === null) return state;
  const side = state.turn;
  const slots = slotsOf(state, side);
  const slot = slots[index];
  const paid: GameState = { ...state, pendingSlot: null };
  if (!slot) return paid;
  if (slot.kind === 'skill') {
    return {
      ...paid,
      board: spendEnergy(paid.board, side, slot.cost),
      skillCasts: { ...paid.skillCasts, [side]: paid.skillCasts[side] + 1 },
    };
  }
  const firstScroll = slots.findIndex((candidate) => candidate.kind === 'scroll');
  const scrolls = paid.scrolls[side].filter((_, held) => held !== index - firstScroll);
  return { ...paid, scrolls: { ...paid.scrolls, [side]: scrolls } };
};

/** Squares whose piece or terrain changed. The board marks them. */
const changedSquares = (before: GameState, after: GameState): number[] => {
  const result = new Set<number>();
  after.board.forEach((piece, square) => {
    if (piece !== before.board[square]) result.add(square);
  });
  const terrainKeys = (rules: Rules): string[] => Object.keys(rules.terrain);
  for (const key of [...terrainKeys(before.rules), ...terrainKeys(after.rules)]) {
    const square = Number(key);
    if (before.rules.terrain[square] !== after.rules.terrain[square]) result.add(square);
  }
  return [...result].sort((a, b) => a - b);
};

/**
 * Stops each piece of the caster that the spell changed, for the rest of the
 * turn: a piece with a new trait, kind, owner, or square, and a piece the
 * spell made. A piece of the other side is not stopped: its owner moves on
 * the next turn, and the stop is only for the turn of the cast.
 */
const stopChangedPieces = (before: Board, after: Board, caster: Side): Board => {
  const known = new Set<Piece>(before.filter((piece) => piece !== null));
  return after.map((piece) => {
    if (!piece || piece.owner !== caster || known.has(piece)) return piece;
    return piece.stoppedTurns >= CAST_STOP_TURNS_CASTER
      ? piece
      : { ...piece, stoppedTurns: CAST_STOP_TURNS_CASTER };
  });
};

/**
 * Runs a spell and records what it touched: the squares, and the log lines.
 * Every piece of the caster it changed is stopped for the rest of the turn.
 */
const resolveCast = (
  state: GameState,
  spell: Spell,
  rng: Rng,
  targets: readonly number[],
): GameState => {
  const cast = spell.cast(
    {
      state: { ...state, hadNoEffect: false, castSteps: [] },
      caster: state.turn,
      rng,
      self: spell,
    },
    targets,
  );
  const written = cast.log.filter((entry) => entry.id >= state.nextLogId);
  const log = cast.log.map((entry) =>
    entry.id >= state.nextLogId ? { ...entry, spell: spell.name, spellId: spell.id } : entry,
  );
  return {
    ...cast,
    board: stopChangedPieces(state.board, cast.board, state.turn),
    log,
    hadNoEffect: false,
    castSteps: [],
    passes: 0,
    castCount: state.castCount + 1,
    lastCast: {
      serial: castSerial(state),
      spellId: spell.id,
      caster: state.turn,
      source: targets[0] ?? null,
      squares: changedSquares(state, cast),
      line: written[0]?.text ?? '',
      hadNoEffect: cast.hadNoEffect,
      steps: cast.castSteps,
      firstEvent: state.nextEventSerial,
    },
  };
};

const beginCast = (state: GameState, spell: Spell, rng: Rng): GameState => {
  if (spell.targetCount === 0) {
    return continueAfterCast(resolveCast(state, spell, rng, []), rng);
  }
  const eligible = spell.eligible(state, state.turn, []);
  if (eligible.length === 0) {
    return continueAfterCast(recordNoEffect(state, spell, 'Nothing could be targeted.'), rng);
  }
  return { ...state, phase: 'aim', pendingSpell: spell.id, pendingTargets: [] };
};

/** A noEffect costs nothing: the caster rolls again, once a turn. */
const grantFreeRoll = (state: GameState): GameState => {
  if (!state.lastCast?.hadNoEffect || state.freeRollUsed) return state;
  return addLog(
    { ...state, pendingRolls: state.pendingRolls + 1, freeRollUsed: true },
    'roll',
    'No effect. The cast is free. Cast again.',
  );
};

const continueAfterCast = (state: GameState, rng: Rng): GameState => {
  const landed: GameState = { ...state, pendingSpell: null, pendingTargets: [] };
  // A cast from a slot is paid for, with an effect or with none. The wheel gives a free roll.
  const cleared =
    landed.mode === 'gods' ? unlockAtHalfStrength(payForSlot(landed)) : grantFreeRoll(landed);
  const ended = settleCrowns(cleared, cleared.lastCast?.line ?? 'A spell did it.');
  if (ended) return ended;
  // A spell may be the whole turn of its caster: no more casts, and no move.
  const cast = cleared.lastCast;
  if (cast && !cast.hadNoEffect && spellById(cast.spellId).endsTurn) {
    const said = addLog(cleared, 'system', `The turn of ${OWNER_NAMES[cleared.turn]} ends.`);
    return endTurn({ ...said, pendingRolls: 0 }, rng);
  }
  if (cleared.pendingRolls > 0) return { ...cleared, phase: 'roll' };
  return enterMovePhase(cleared, rng);
};

export const eligibleTargets = (state: GameState): number[] => {
  if (state.phase !== 'aim' || !state.pendingSpell) return [];
  return spellById(state.pendingSpell).eligible(state, state.turn, state.pendingTargets);
};

/**
 * The squares whose pieces a pick of this square removes, for a spell that names them.
 * The board marks them before the click.
 */
export const removedByPick = (state: GameState, square: number): readonly number[] => {
  if (state.phase !== 'aim' || !state.pendingSpell) return [];
  return spellById(state.pendingSpell).removes?.(state, state.turn, square) ?? [];
};

/** Does the spell the side aims take a region with its pick? */
export const aimsAtRegion = (state: GameState): boolean =>
  state.phase === 'aim' &&
  state.pendingSpell !== null &&
  spellById(state.pendingSpell).region !== undefined;

/** The region a pick of this square takes, for a spell that aims at a region. Null for any other spell. */
export const regionOfPick = (state: GameState, square: number): PickRegion | null => {
  if (state.phase !== 'aim' || !state.pendingSpell) return null;
  return spellById(state.pendingSpell).region?.(state, state.turn, square) ?? null;
};

/**
 * Does the hand hold the card of the last roll? The card of an aimed spell
 * stays until the last pick, because the player reads it to aim. The card of
 * a spell with no target stays for the ply of the roll, because it is the
 * record of what the roll did.
 */
export const isCardHeld = (state: GameState): boolean => heldCard(state) !== null;

/**
 * The card the hand holds, with the side that holds it. In Godsmode it is the
 * spell the side aims, from the slot until the last pick.
 */
export const heldCard = (state: GameState): { spellId: SpellId; caster: Side } | null => {
  if (state.phase === 'over') return null;
  if (state.mode === 'gods') {
    return state.phase === 'aim' && state.pendingSpell
      ? { spellId: state.pendingSpell, caster: state.turn }
      : null;
  }
  const { lastRoll, lastCast } = state;
  if (lastRoll === null || lastRoll.spellId === null || lastRoll.ply !== state.ply) return null;
  const isCast = lastCast !== null && lastCast.serial === lastRoll.serial;
  if (isCast && spellById(lastRoll.spellId).targetCount > 0) return null;
  return { spellId: lastRoll.spellId, caster: lastRoll.caster };
};

export const aimAt = (state: GameState, square: number, rng: Rng): GameState => {
  if (state.phase !== 'aim' || !state.pendingSpell) return state;
  if (!eligibleTargets(state).includes(square)) return state;
  const spell = spellById(state.pendingSpell);
  const picked = [...state.pendingTargets, square];
  if (picked.length < spell.targetCount) {
    const partial: GameState = { ...state, pendingTargets: picked };
    if (spell.eligible(partial, state.turn, picked).length === 0) {
      return continueAfterCast(recordNoEffect(partial, spell, 'There was nowhere to go.'), rng);
    }
    return partial;
  }
  return continueAfterCast(resolveCast(state, spell, rng, picked), rng);
};

// ---- Moving ------------------------------------------------------------------

/** Moves the side may make from a square now, with the mustCapture rule applied. */
export const legalMovesFrom = (state: GameState, square: number): Move[] => {
  if (state.phase !== 'move') return [];
  const piece = state.board[square];
  if (!piece || piece.owner !== state.turn) return [];
  if (state.mustMovePieceId !== null && piece.id !== state.mustMovePieceId) return [];
  return generateMoves(positionOf(state), state.turn).filter((move) => move.from === square);
};

const describeMove = (mover: Piece, move: Move, target: Piece | null, bounced: boolean): string => {
  const who = describePiece(mover, move.from);
  if (!target) return `${who} moved to ${squareName(move.to)}.`;
  const victim = describePiece(target, move.to);
  if (bounced) {
    return `${who} struck ${victim}, but it had a spare life. The ${KIND_NAMES[mover.kind]} bounced home.`;
  }
  return `${who} took ${victim}.`;
};

export const playMove = (state: GameState, requested: Move, rng: Rng): GameState => {
  if (state.phase !== 'move') return state;
  const mover = state.board[requested.from];
  if (!mover || mover.owner !== state.turn) return state;
  if (state.mustMovePieceId !== null && mover.id !== state.mustMovePieceId) return state;
  const position = positionOf(state);
  if (!isMoveLegal(position, requested, state.turn)) return state;

  let move = requested;
  let next = state;
  const randomNextMove = mover.randomNextMove;
  if (randomNextMove) move = rng.pick(movesForPiece(position, requested.from));

  const outcome = applyMove(position, move);
  // A pushed piece is not a target. The push is a note, and the move reads as a move.
  const target = outcome.pushed ? null : state.board[move.to];
  next = { ...withMoveBoard(next, outcome), lastMove: move, passes: 0 };
  next = addLog(
    next,
    target ? 'capture' : 'move',
    describeMove(mover, move, target, outcome.bounced),
  );
  if (randomNextMove) {
    next = recordEvent(
      next,
      'A random move',
      `${describePiece(mover, move.from)} meant to go to ${squareName(requested.to)}. It went to ${squareName(move.to)}.`,
      [move.to],
      'enemy',
      [requested.to],
    );
  }
  if (outcome.bounced && target) {
    next = recordEvent(
      next,
      'An extra life is lost',
      `${describePiece(target, move.to)} lost a life. ${describePiece(mover, move.from)} bounced home.`,
      [move.to],
      'ally',
      [move.from],
    );
  }
  next = recordNotes(next, outcome.notes);
  if (outcome.captured.length > 0) {
    next = { ...next, captured: [...next.captured, ...outcome.captured] };
  }
  if (outcome.promoted) {
    const landed = findPieceSquare(next.board, mover.id);
    next = recordEvent(
      next,
      'Promotion',
      `${describePiece(mover, move.from)} reached the far side and became a queen.`,
      landed === null ? [] : [landed],
      'ally',
    );
  }

  const royal = outcome.pushed ? undefined : outcome.captured.find((piece) => piece.royal);
  const endLine = royal
    ? `${describePiece(mover, move.from)} took ${describePiece(royal, move.to)}.`
    : (lastEventText(next) ?? 'The last royal piece fell.');
  const ended = settleCrowns(next, endLine);
  if (ended) return ended;

  const landed = findPieceSquare(next.board, mover.id);
  if (mover.movesTwice && landed !== null) {
    const stillMobile = movesForPiece(positionOf(next), landed).length > 0;
    next = {
      ...next,
      board: next.board.map((p) => (p?.id === mover.id ? { ...p, movesTwice: false } : p)),
    };
    if (stillMobile) {
      next = addLog(
        next,
        'system',
        `${describePiece(mover, landed)} is still buzzing. It moves again.`,
      );
      return { ...next, mustMovePieceId: mover.id, phase: 'move' };
    }
  }
  // A capture gives the piece one more move. The flag goes, so the second move gives no third.
  const tookPiece = target !== null && !outcome.bounced;
  if (mover.capturesAgain && tookPiece && landed !== null) {
    next = {
      ...next,
      board: next.board.map((p) => (p?.id === mover.id ? { ...p, capturesAgain: false } : p)),
    };
    if (movesForPiece(positionOf(next), landed).length > 0) {
      next = addLog(
        next,
        'system',
        `${describePiece(mover, landed)} took a piece. It moves again.`,
      );
      return {
        ...next,
        mustMovePieceId: mover.id,
        moveAgainSpellId: CAPTURE_AGAIN_SPELL_ID,
        phase: 'move',
      };
    }
  }
  return endTurn(next, rng);
};

const declareWinner = (state: GameState, winner: Side, endLine: string): GameState => {
  const announced = addLog(
    state,
    'system',
    `${OWNER_NAMES[winner]} wins. ${OWNER_NAMES[otherSide(winner)]} has no royal piece left.`,
  );
  return { ...announced, winner, endLine, phase: 'over' };
};

/**
 * The side gives up, and the other side wins. On either turn, in any phase
 * before the end. Each royal piece of the side explodes and leaves the board.
 * Rob, 2026-10-15: "We need an easy \"surrender\" option that makes all
 * royal pieces explode and ends the game in the enemy's favor." Until then
 * the board stayed as it was.
 */
export const concede = (state: GameState, side: Side): GameState => {
  if (state.phase === 'over') return state;
  const winner = otherSide(side);
  const royals = state.board.flatMap((piece, square) =>
    piece?.owner === side && piece.royal ? [square] : [],
  );
  const cleared: GameState = {
    ...state,
    board: royals.reduce((board, square) => withPiece(board, square, null), state.board),
    captured: [...state.captured, ...royals.flatMap((square) => state.board[square] ?? [])],
  };
  const exploded =
    royals.length === 0
      ? cleared
      : recordEvent(
          cleared,
          'Royal pieces explode',
          `${OWNER_NAMES[side]} gave up. ${royals
            .map((square) => describePiece(state.board[square] as Piece, square))
            .join(', ')} exploded.`,
          royals,
          'capture',
        );
  const line = `${OWNER_NAMES[side]} gave up.`;
  const announced = addLog(exploded, 'system', `${line} ${OWNER_NAMES[winner]} wins.`);
  return { ...announced, winner, endLine: line, phase: 'over' };
};

const declareDraw = (state: GameState, endLine: string): GameState => {
  const announced = addLog(state, 'system', 'Both sides lost their last royal piece. A draw.');
  return { ...announced, winner: null, endLine, phase: 'over' };
};

/** Ends the match when a side has no royal piece. Null when play goes on. */
const settleCrowns = (state: GameState, endLine: string): GameState | null => {
  if (bothCrownsFallen(state.board)) return declareDraw(state, endLine);
  const winner = winnerOf(state.board);
  return winner ? declareWinner(state, winner, endLine) : null;
};

/** The pawns of the side that carry no caster. A caster that loses its piece goes to one of them. */
const sparePawns = (board: Board, side: Side): number[] =>
  board.flatMap((piece, square) =>
    piece?.owner === side && piece.kind === 'pawn' && piece.casterId === null ? [square] : [],
  );

/**
 * For each caster that had a living piece of its side before an action and
 * has none after it: the caster goes to a random pawn of the side that
 * carries no caster, and an event says so. A piece that took the caster to
 * the other side loses it. With no such pawn, the caster stays gone. Rob,
 * 2026-10-15: "When the pawn carrying them dies, they \"bounce\" to a random
 * available \"empty\" pawn (i.e. not carrying a caster). So the player can
 * still run out of space but it takes longer".
 */
const bounceCasters = (before: GameState, after: GameState, rng: Rng): GameState => {
  let next = after;
  for (const side of SIDES) {
    const had = livingCasterIds(before.board, side);
    for (const group of casterGroupsOf(before, side)) {
      const id = group.caster.id;
      if (!had.has(id) || livingCasterIds(next.board, side).has(id)) continue;
      const board = next.board.map((piece) =>
        piece?.casterId === id ? { ...piece, casterId: null } : piece,
      );
      const spares = sparePawns(board, side);
      if (spares.length === 0) {
        next = { ...next, board };
        continue;
      }
      const square = rng.pick(spares);
      const pawn = board[square] as Piece;
      next = recordEvent(
        { ...next, board: withPiece(board, square, { ...pawn, casterId: id }) },
        'A caster moves',
        `${group.caster.name} of ${OWNER_NAMES[side]} goes to ${describePiece(pawn, square)}.`,
        [square],
      );
    }
  }
  return next;
};

/**
 * For each caster that had a living piece before an action and has none
 * after it: the caster goes to a spare pawn, see `bounceCasters`. When it
 * has none, takes the scrolls in its slots from the side, and records an
 * event. Records an event for each pitch that is gone too. The action may be
 * a capture, a blast, a deadly square, or a spell, so this compares the two
 * states and does not ask how. Garrett, 2026-10-13, on the scrolls of a
 * caster that is gone: "scrolls lost at death yeah".
 */
export const settleLostRoles = (before: GameState, after: GameState, rng: Rng): GameState => {
  if (!after.casters || after === before) return after;
  let next = after.phase === 'over' ? after : bounceCasters(before, after, rng);
  for (const side of SIDES) {
    const had = livingCasterIds(before.board, side);
    const has = livingCasterIds(next.board, side);
    const groups = casterGroupsOf(before, side);
    const held = scrollsByCaster(groups, next.scrolls[side]);
    const isLost = (id: string): boolean => had.has(id) && !has.has(id);
    if (groups.some((group) => isLost(group.caster.id))) {
      const kept = groups.flatMap((group, place) =>
        isLost(group.caster.id) ? [] : (held[place] ?? []),
      );
      next = { ...next, scrolls: { ...next.scrolls, [side]: kept } };
    }
    for (const [place, group] of groups.entries()) {
      if (!isLost(group.caster.id)) continue;
      const lost = held[place]?.length ?? 0;
      const scrolls =
        lost === 0
          ? ''
          : lost === 1
            ? ' The scroll it held is gone too.'
            : ` The ${lost} scrolls it held are gone too.`;
      next = recordEvent(
        next,
        'A caster is gone',
        `${group.caster.name} of ${OWNER_NAMES[side]} has no piece on the board. Its skills and its scroll slots are gone.${scrolls}`,
        [],
        'capture',
      );
    }
    // A pitch with no piece left to give it.
    const hadPitches = livingPitches(before.board, side);
    const hasPitches = livingPitches(after.board, side);
    for (const pitch of hadPitches) {
      if (hasPitches.has(pitch)) continue;
      next = recordEvent(
        next,
        'A pitch is gone',
        `${OWNER_NAMES[side]} has no piece that gives the ${pitch} pitch.`,
        [],
        'capture',
      );
    }
  }
  return next;
};

export const turnLabel = (state: GameState): string =>
  `Turn ${sideTurnNumber(state)}, ${OWNER_NAMES[state.turn]}`;
